import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { ModalShell } from '@/components/ui/ModalShell'
import { Tooltip } from '@/components/ui/Tooltip'
import { morphToast } from '@/components/ui/Toast'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { ChevronDownIcon, ChevronUpIcon, EyeIcon } from '@/components/ui/icons'
import { useModalStore } from '@/hooks/useModalStore'
import { useCommitsStore } from '@/stores/commitsStore'
import { useDraftStore } from '@/stores/draftStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { useConfirmStore } from '@/hooks/useConfirmStore'
import { useProjectCan } from '@/hooks/useProjectCan'
import type { CommitListItem } from '@/api/types'

// ── Constants for precise layout ──────────────────────────────────────────────

const TIMELINE_COLOR = 'var(--teal-dark)'     // bright teal/cyan
const AVATAR_SIZE = 48
const TIMELINE_WIDTH = 2
const AVATAR_COL_WIDTH = 64  // wider than avatar so line is comfortably centered
const HOVER_RING_COLOR = '#A78BFA'             // violet hover indicator

// ── Main component ────────────────────────────────────────────────────────────

export function VersionsModal() {
  const { t } = useTranslation()
  const open = useModalStore((s) => s.versionsOpen)
  const close = useModalStore((s) => s.closeVersions)
  const openCreateCommit = useModalStore((s) => s.openCreateCommit)
  const language = useSettingsStore((s) => s.language)

  const currentProjectId = useDraftStore((s) => s.currentProjectId)
  const mode = useDraftStore((s) => s.mode)
  const viewingCommitId = useDraftStore((s) => s.viewingCommitId)
  const { commits, historyTruncated, loading, loadCommits, restoreCommit, returnToDraft } = useCommitsStore()
  const showConfirm = useConfirmStore((s) => s.show)
  const canCreateCommit = useProjectCan('create_commit')

  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const [hoveredAvatarId, setHoveredAvatarId] = useState<number | null>(null)
  const [returningToDraft, setReturningToDraft] = useState(false)

  useEffect(() => {
    if (open && currentProjectId) {
      void loadCommits(currentProjectId)
      setExpandedIds(new Set())
    }
  }, [open, currentProjectId, loadCommits])

  function toggleExpand(id: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function handleView(commit: CommitListItem) {
    if (!currentProjectId) return
    showConfirm(
      t('versions.viewTitle', 'Visualizar versão'),
      t('versions.viewQuestion', {
        name: commit.name,
        defaultValue: `Deseja visualizar a versão "${commit.name}"? As alterações não salvas serão substituídas pelo conteúdo desta versão.`,
      }),
      async () => {
        try {
          await restoreCommit(currentProjectId, commit.id)
          morphToast.success(t('versions.viewed', {
            name: commit.name,
            defaultValue: `Visualizando "${commit.name}"`,
          }))
          close()
        } catch {
          morphToast.error(t('common.error', 'Erro ao visualizar versão'))
        }
      },
    )
  }

  async function handleReturnToDraft() {
    if (!currentProjectId) return
    setReturningToDraft(true)
    try {
      await returnToDraft(currentProjectId)
      morphToast.success(t('versions.backToDraftDone', 'Voltamos para o rascunho atual.'))
      close()
    } catch {
      morphToast.error(t('versions.backToDraftError', 'Erro ao voltar para o rascunho atual'))
    } finally {
      setReturningToDraft(false)
    }
  }

  const draftCard = (() => {
    const isActive = mode !== 'view'

    return (
      <div className="flex items-start gap-4">
        <div
          className="relative flex shrink-0 items-start justify-center"
          style={{ width: AVATAR_COL_WIDTH }}
        >
          <div
            className="relative z-10 flex shrink-0 items-center justify-center rounded-full"
            style={{
              width: AVATAR_SIZE,
              height: AVATAR_SIZE,
              border: isActive ? `2px solid ${TIMELINE_COLOR}` : `2px dashed ${TIMELINE_COLOR}`,
              background: isActive
                ? `${TIMELINE_COLOR}22`
                : 'var(--bg-input)',
              color: TIMELINE_COLOR,
              boxShadow: isActive ? `0 0 12px ${TIMELINE_COLOR}55` : 'none',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 15V5.5L9 2l6 3.5V15" />
              <rect x="6" y="9" width="6" height="6" rx="0.5" />
            </svg>
          </div>
        </div>

        {isActive ? (
          <div
            className="flex-1 min-w-0 rounded-xl"
            style={{
              background: `${TIMELINE_COLOR}14`,
              border: `1.5px solid ${TIMELINE_COLOR}`,
              padding: '12px 16px',
              minHeight: AVATAR_SIZE,
            }}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span
                    className="font-bold leading-tight"
                    style={{ fontSize: 16, color: TIMELINE_COLOR }}
                  >
                    {t('versions.draftLabel', 'Rascunho')}
                  </span>
                  <span
                    className="rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide"
                    style={{ background: TIMELINE_COLOR, color: '#000' }}
                  >
                    {t('versions.current', 'Atual')}
                  </span>
                </div>
                <p
                  className="mt-1 italic"
                  style={{ fontSize: 11, color: 'var(--text-dim)' }}
                >
                  {t('versions.draftDescription', 'Versão em edição')}
                </p>
              </div>
              <Button
                onClick={() => { close(); openCreateCommit() }}
                disabled={!currentProjectId || !canCreateCommit}
                style={{ background: TIMELINE_COLOR, borderColor: TIMELINE_COLOR, color: '#000' }}
              >
                {t('versions.saveNew', 'Salvar versão')}
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            disabled={!currentProjectId || returningToDraft}
            onClick={() => { void handleReturnToDraft() }}
            className="flex-1 min-w-0 rounded-xl text-left transition-colors disabled:opacity-50"
            style={{
              background: 'var(--bg-input)',
              border: `1.5px dashed ${TIMELINE_COLOR}`,
              padding: '12px 16px',
              minHeight: AVATAR_SIZE,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'color-mix(in srgb, var(--bg-input) 80%, var(--text) 20%)' }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-input)' }}
          >
            <span
              className="block font-bold leading-tight"
              style={{ fontSize: 16, color: TIMELINE_COLOR }}
            >
              {t('versions.draftLabel', 'Rascunho')}
            </span>
            <p
              className="mt-1 italic"
              style={{ fontSize: 11, color: 'var(--text-dim)' }}
            >
              {t('versions.backToDraft', 'Voltar ao rascunho atual')}
            </p>
          </button>
        )}
      </div>
    )
  })()

  return (
    <ModalShell
      open={open}
      onClose={close}
      title={t('versions.title')}
      widthClassName="max-w-[60vw]"
      heightClassName="max-h-[80vh]"
      footer={null}
    >
      <div>

        {historyTruncated && (
          <div
            className="mb-4 rounded-lg border px-3 py-2 text-[11px]"
            style={{
              borderColor: 'var(--color-warning-border)',
              background: 'var(--color-warning-bg)',
              color: 'var(--color-warning)',
            }}
          >
            {t('versions.historyTruncated', 'O histórico está limitado pelo plano atual. Faça upgrade para ver todas as versões.')}
          </div>
        )}

        {loading ? (
          <div className="py-8 text-center text-[13px]" style={{ color: 'var(--text-dim)' }}>
            {t('common.loading', 'Carregando...')}
          </div>
        ) : (
          // ── Outer wrapper with continuous timeline line behind everything ──
          <div className="relative">
            {/* Continuous timeline line — absolute, spans whole list, behind avatars */}
            <div
              className="absolute"
              style={{
                left: AVATAR_COL_WIDTH / 2 - TIMELINE_WIDTH / 2,
                top: AVATAR_SIZE / 2,
                bottom: AVATAR_SIZE / 2,
                width: TIMELINE_WIDTH,
                background: TIMELINE_COLOR,
                opacity: 0.7,
                zIndex: 0,
              }}
            />

            <div className="relative flex flex-col gap-2" style={{ zIndex: 1 }}>
              {draftCard}

              {commits.length === 0 ? (
                <div
                  className="rounded-xl border border-dashed px-4 py-8 text-center text-[11px]"
                  style={{ borderColor: 'var(--border)', color: 'var(--text-dim)' }}
                >
                  {t('versions.empty', 'Nenhuma versão salva ainda.')}
                </div>
              ) : null}

              {commits.map((commit) => {
                const isExpanded = expandedIds.has(commit.id)
                const isHovered = hoveredAvatarId === commit.id
                const author = commit.created_by
                const authorId = author?.id ?? commit.id
                const authorName = author?.name ?? ''
                const authorEmail = author?.email ?? ''
                const dateStr = new Intl.DateTimeFormat(language, {
                  dateStyle: 'short',
                  timeStyle: 'short',
                }).format(new Date(commit.created_at))

                return (
                  <div key={commit.id} className="flex items-start gap-4">

                    {/* ── Avatar column ── */}
                    <div
                      className="relative flex shrink-0 items-start justify-center"
                      style={{ width: AVATAR_COL_WIDTH }}
                    >
                      <Tooltip content={authorName || '—'} delayMs={300}>
                        <div
                          className="relative"
                          onMouseEnter={() => setHoveredAvatarId(commit.id)}
                          onMouseLeave={() => setHoveredAvatarId(null)}
                        >
                          {/* hover ring (violet square outline) */}
                          <div
                            className="pointer-events-none absolute transition-opacity"
                            style={{
                              inset: -5,
                              borderRadius: 8,
                              border: `2px solid ${HOVER_RING_COLOR}`,
                              opacity: isHovered ? 1 : 0,
                            }}
                          />
                          {/* the avatar circle */}
                          <div
                            className="relative z-10 shrink-0 cursor-default select-none rounded-full"
                            style={{
                              border: `2px solid ${TIMELINE_COLOR}`,
                              boxShadow: `0 0 10px ${TIMELINE_COLOR}55`,
                            }}
                          >
                            <UserAvatar
                              userId={authorId}
                              name={authorName}
                              email={authorEmail}
                              size={AVATAR_SIZE}
                            />
                          </div>
                        </div>
                      </Tooltip>
                    </div>

                    {/* ── Card (rounded borders, contains commit info + chevron) ── */}
                    <div
                      className="flex-1 min-w-0 rounded-xl border cursor-pointer"
                      style={{
                        background: 'var(--bg-input)',
                        borderColor: viewingCommitId === commit.id ? TIMELINE_COLOR : 'var(--border)',
                        padding: '12px 16px',
                        minHeight: AVATAR_SIZE,
                      }}
                      onClick={() => toggleExpand(commit.id)}
                    >
                      {/* Header: [name + date]  ⇿  [chip]  [eye]  [chevron] */}
                      <div className="flex items-center justify-between gap-3">
                        {/* Left: name + date stacked */}
                        <div className="flex min-w-0 flex-1 flex-col">
                          <div className="flex items-center gap-2">
                            <Tooltip content={commit.name} delayMs={600}>
                              <span
                                className="block min-w-0 truncate font-bold leading-tight"
                                style={{ fontSize: 16, color: 'var(--text)' }}
                              >
                                {commit.name}
                              </span>
                            </Tooltip>
                            {viewingCommitId === commit.id && (
                              <span
                                className="shrink-0 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide"
                                style={{ background: TIMELINE_COLOR, color: '#000' }}
                              >
                                {t('versions.current', 'Atual')}
                              </span>
                            )}
                          </div>
                          <p
                            className="mt-1 italic"
                            style={{ fontSize: 11, color: 'var(--text-dim)' }}
                          >
                            {dateStr}
                          </p>
                        </div>

                        {/* Right: eye button + chevron */}
                        <div className="flex shrink-0 items-center gap-2">
                          <Tooltip content={t('versions.view', 'Visualizar')} delayMs={300}>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleView(commit) }}
                              className="inline-flex items-center justify-center rounded-md transition-colors"
                              style={{
                                width: 28,
                                height: 28,
                                border: `1px solid ${TIMELINE_COLOR}`,
                                color: TIMELINE_COLOR,
                                background: 'transparent',
                              }}
                              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = `${TIMELINE_COLOR}1f` }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent' }}
                            >
                              <EyeIcon />
                            </button>
                          </Tooltip>
                          <div style={{ color: 'var(--text-muted)' }}>
                            {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                          </div>
                        </div>
                      </div>

                      {/* Expanded body */}
                      {isExpanded && (
                        <>
                          {/* description */}
                          {commit.description ? (
                            <p
                              className="mt-2 whitespace-pre-wrap leading-relaxed"
                              style={{ fontSize: 13, color: 'var(--text)' }}
                            >
                              {commit.description}
                            </p>
                          ) : null}

                        </>
                      )}
                    </div>

                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  )
}
